import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

export class QrCode {
  #prefix;

  constructor({ prefix }) {
    this.#prefix = prefix;
    this.format = Html5QrcodeSupportedFormats.QR_CODE;
  }

  isValidFormat(decodedResult) {
    const decodedFormat = decodedResult?.result?.format?.format;

    return decodedFormat === this.format;
  }

  isValid(decodedResult) {
    const decodedText = decodedResult?.decodedText ?? "";
    const isValidText = !this.#prefix || decodedText.startsWith(this.#prefix);

    return this.isValidFormat(decodedResult) && isValidText;
  }
}

export class Barcode {
  #prefix;
  #length;

  constructor({ prefix, length }) {
    this.#prefix = prefix;
    this.#length = length;
    this.format = Html5QrcodeSupportedFormats.CODE_128;
  }

  isValidFormat(decodedResult) {
    const decodedFormat = decodedResult?.result?.format?.format;

    return decodedFormat === this.format;
  }

  isValid(decodedResult) {
    const decodedText = decodedResult?.decodedText ?? "";
    const isValidText =
      decodedText.length === this.#length &&
      decodedText.startsWith(this.#prefix);

    return this.isValidFormat(decodedResult) && isValidText;
  }
}

export class Scanner {
  #html5QrCode;

  constructor(componentId, codes = {}) {
    const that = this;
    Object.entries(codes).forEach(([key, code]) => {
      that[key] = code;
    });

    const formatsToSupport = Object.values(codes).map(({ format }) => format);

    this.#html5QrCode = new Html5Qrcode(componentId, { formatsToSupport });
  }

  async stop() {
    try {
      await this.#html5QrCode.stop();
    } catch (err) {
      console.debug("Can't stop scanner:", err);
    }
  }

  scan(code, onSuccess, onError = () => {}) {
    this.#start((decodedText, decodedResult) => {
      if (code.isValid(decodedResult)) {
        onSuccess(decodedText);
      } else {
        onError(decodedResult);
      }
    });
  }

  async toggleTorch() {
    if (!this.#torchFeature().isSupported()) return;

    const oldValue = await this.#torchFeature().value();
    return await this.#torchFeature().apply(!oldValue);
  }

  #torchFeature() {
    try {
      return this.#html5QrCode
        .getRunningTrackCameraCapabilities()
        .torchFeature();
    } catch (err) {
      // This is the torchFeature interface
      return {
        apply: async () => false,
        value: async () => null,
        isSupported: () => false,
      };
    }
  }

  async #start(onSuccess) {
    try {
      await this.#html5QrCode.start(
        { facingMode: "environment" },
        { fps: 10 },
        onSuccess,
      );

      this.#html5QrCode.applyVideoConstraints({
        focusMode: "continuous",
        advanced: [{ zoom: 2 }],
      });
    } catch (err) {
      console.debug("Can't start scanner:", err);
    }
  }
}
